import { useEffect, useRef, useState } from "react";
import { Eraser } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatErr, downloadBlob } from "@/lib/api";
import { originUrl, openExternal } from "@/lib/native";

export const SignaturePad = ({ onChange }) => {
  const ref = useRef(null);
  const drawing = useRef(false);
  useEffect(() => {
    const ctx = ref.current.getContext("2d");
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#0F172A";
  }, []);
  const pos = (e) => {
    const r = ref.current.getBoundingClientRect();
    return [((e.clientX - r.left) * ref.current.width) / r.width, ((e.clientY - r.top) * ref.current.height) / r.height];
  };
  const down = (e) => { drawing.current = true; const ctx = ref.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(...pos(e)); ref.current.setPointerCapture(e.pointerId); };
  const move = (e) => { if (!drawing.current) return; const ctx = ref.current.getContext("2d"); ctx.lineTo(...pos(e)); ctx.stroke(); };
  const up = () => { if (drawing.current) { drawing.current = false; onChange(ref.current.toDataURL("image/png")); } };
  const clear = () => { ref.current.getContext("2d").clearRect(0, 0, ref.current.width, ref.current.height); onChange(""); };
  return (
    <div>
      <canvas ref={ref} width={600} height={180} data-testid="signature-pad" onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up}
        className="h-36 w-full touch-none rounded-md border border-dashed border-stone-400 bg-white" />
      <button type="button" onClick={clear} data-testid="signature-clear" className="mt-1 inline-flex items-center gap-1 text-xs text-stone-500 hover:text-stone-900"><Eraser className="h-3 w-3" />Clear</button>
    </div>
  );
};

export const SignOfferDialog = ({ offer, open, onOpenChange, onSigned, countersign = false }) => {
  const [name, setName] = useState("");
  const [sig, setSig] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = countersign
        ? await api.post(`/offers/${offer.id}/countersign`, { signed_name: name, signature: sig, agreed })
        : await api.post(`/offers/${offer.id}/respond`, { decision: "accept", signed_name: name, signature: sig, agreed });
      toast.success(countersign ? "Agreement countersigned" : "Offer accepted and contract signed");
      onSigned(data);
      onOpenChange(false);
    } catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="sign-offer-dialog" className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{countersign ? "Countersign" : "Sign & accept"}: {offer?.position}</DialogTitle>
          <DialogDescription>{countersign ? "Your signature is added to the PDF so it carries both signatures." : "A PDF of the agreed terms with your signature will be saved for you, the business and our team."}</DialogDescription>
        </DialogHeader>
        <div className="max-h-40 overflow-y-auto whitespace-pre-wrap rounded-md bg-stone-50 p-4 text-sm text-stone-700">{offer?.license_terms || "Standard placement terms apply."}</div>
        <div className="space-y-1.5"><Label>Full legal name</Label><Input data-testid="sign-name-input" value={name} onChange={(e) => setName(e.target.value)} /></div>
        <div className="space-y-1.5"><Label>Draw your signature</Label><SignaturePad onChange={setSig} /></div>
        <label className="flex items-start gap-2 text-sm text-stone-700">
          <Checkbox data-testid="sign-agree-checkbox" checked={agreed} onCheckedChange={(v) => setAgreed(!!v)} className="mt-0.5" />
          I have read and agree to the offer and licence terms, and I accept that this electronic signature is legally binding.
        </label>
        <DialogFooter><Button data-testid="sign-submit-button" disabled={busy || !name.trim() || !sig || !agreed} onClick={submit}>{busy ? "Signing…" : countersign ? "Countersign agreement" : "Sign & accept offer"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const ContractButton = ({ offer, className = "" }) => offer?.contract ? (
  <span className={className}>
    <button data-testid={`contract-download-${offer.id}`} className="text-sm font-medium text-slate-900 underline"
      onClick={() => downloadBlob(`/offers/${offer.id}/contract`, `agreement-${offer.id.slice(0, 8)}.pdf`).catch((e) => toast.error(formatErr(e)))}>
      Download {offer.contract.countersigned ? "fully signed" : "signed"} contract (PDF)
    </button>
    <span data-testid={`contract-status-${offer.id}`} className={`ml-2 rounded-full px-2 py-0.5 text-[10px] font-mono uppercase ${offer.contract.countersigned ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
      {offer.contract.countersigned ? "Both parties signed" : "Awaiting business countersign"}
    </span>
  </span>
) : null;

export const FeeStatus = ({ offer, canPay = false }) => {
  const [busy, setBusy] = useState(false);
  if (!(offer?.licensing_fee > 0)) return null;
  const paid = offer.fee_status === "paid";
  const ready = offer.contract?.countersigned;
  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/offers/${offer.id}/pay-fee`, { origin_url: originUrl() });
      await openExternal(data.checkout_url);
    } catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <span data-testid={`fee-status-${offer.id}`} className={`rounded-full px-2 py-0.5 text-[10px] font-mono uppercase ${paid ? "bg-emerald-50 text-emerald-700" : "bg-stone-100 text-stone-600"}`}>
        Placement fee ${offer.licensing_fee} USD · {paid ? "Paid" : ready ? "Due" : "Due after both sign"}
      </span>
      {canPay && !paid && ready && (
        <Button size="sm" data-testid={`pay-fee-button-${offer.id}`} disabled={busy} onClick={pay} className="bg-amber-600 hover:bg-amber-700">
          {busy ? "Opening checkout…" : `Pay placement fee $${offer.licensing_fee}`}
        </Button>
      )}
    </div>
  );
};

export const CountersignButton = ({ offer, onSigned }) => {
  const [open, setOpen] = useState(false);
  if (!offer?.contract || offer.contract.countersigned) return null;
  return (
    <>
      <Button size="sm" data-testid={`countersign-button-${offer.id}`} onClick={() => setOpen(true)} className="mt-2">Countersign agreement</Button>
      <SignOfferDialog offer={offer} open={open} onOpenChange={setOpen} onSigned={onSigned} countersign />
    </>
  );
};
